
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
import { Coins, ShieldCheck, Repeat, Lock, ArrowUpRight, Star } from 'lucide-react';

export const GhostTokenSuiteDoc = () => (
    <div className="space-y-12 animate-fade-in">
        <section>
            <p className="text-lg text-stone-600 dark:text-stone-300 leading-relaxed font-light">
                The Ghost Token Suite is the native financial foundation of Ghost Protocol. It enables high-performance cross-chain DeFi, automated treasury management, and AI-driven governance through a trifecta of specialized assets.
            </p>
        </section>

        {/* Token Ecosystem Overview */}
        <section className="grid md:grid-cols-3 gap-6">
            {[
                { symbol: 'GHOST', type: 'Native Asset (Sui)', icon: Coins, desc: 'The heart of the ecosystem. Used for POS settlement, transfer fees, and rewards on the Sui network.' },
                { symbol: 'wGHOST', type: 'Wrapped (Solana)', icon: Repeat, desc: '1:1 backed derivative on Solana, enabling high-frequency retail payments and Jupiter DEX integration.' },
                { symbol: 'veGHOST', type: 'Governance', icon: ShieldCheck, desc: 'Voting Escrow GHOST. Minted by locking GHOST to participate in DAO governance and protocol fee split.' },
            ].map((token, i) => (
                <div key={i} className="p-6 rounded-2xl bg-white dark:bg-white/[0.02] border border-stone-200 dark:border-white/10 group hover:border-nobel-gold/50 transition-all shadow-sm">
                    <div className="w-10 h-10 rounded-xl bg-stone-100 dark:bg-white/5 flex items-center justify-center text-stone-900 dark:text-white shadow-inner mb-4 group-hover:bg-nobel-gold group-hover:text-black transition-colors">
                        <token.icon size={20} />
                    </div>
                    <h4 className="font-bold text-lg text-stone-900 dark:text-white mb-1 font-serif">{token.symbol}</h4>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-3">{token.type}</div>
                    <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed">{token.desc}</p>
                </div>
            ))}
        </section>

        {/* Tokenomics Table */}
        <section>
            <h3 className="text-2xl font-serif font-bold text-stone-900 dark:text-white mb-6 border-b border-stone-100 dark:border-white/10 pb-4">Tokenomics Architecture</h3>
            <div className="overflow-x-auto rounded-2xl border border-stone-200 dark:border-white/10 shadow-sm bg-white dark:bg-black/20">
                <table className="w-full text-sm text-left">
                    <thead className="bg-stone-50 dark:bg-white/5 text-stone-500 uppercase text-[10px] font-bold tracking-widest">
                        <tr>
                            <th className="px-6 py-4">Feature</th>
                            <th className="px-6 py-4">GHOST</th>
                            <th className="px-6 py-4">wGHOST</th>
                            <th className="px-6 py-4">veGHOST</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100 dark:divide-white/5 text-stone-600 dark:text-stone-300">
                        <tr>
                            <td className="px-6 py-4 font-bold bg-stone-50/30 dark:bg-white/[0.01]">Supply Model</td>
                            <td className="px-6 py-4">20,000,000,000</td>
                            <td className="px-6 py-4">1:1 Backed</td>
                            <td className="px-6 py-4 italic">Dynamic (Locked)</td>
                        </tr>
                        <tr>
                            <td className="px-6 py-4 font-bold bg-stone-50/30 dark:bg-white/[0.01]">Standard Fee</td>
                            <td className="px-6 py-4 text-nobel-gold font-bold">2.5% (Sender)</td>
                            <td className="px-6 py-4">0%</td>
                            <td className="px-6 py-4">0%</td>
                        </tr>
                        <tr>
                            <td className="px-6 py-4 font-bold bg-stone-50/30 dark:bg-white/[0.01]">Deflationary Split</td>
                            <td className="px-6 py-4 text-xs">60% Burn / 40% DAO</td>
                            <td className="px-6 py-4 text-xs">N/A</td>
                            <td className="px-6 py-4 text-xs">N/A</td>
                        </tr>
                        <tr>
                            <td className="px-6 py-4 font-bold bg-stone-50/30 dark:bg-white/[0.01]">Governance Power</td>
                            <td className="px-6 py-4 text-stone-400">None</td>
                            <td className="px-6 py-4 text-stone-400">None</td>
                            <td className="px-6 py-4 font-bold text-emerald-500 text-center">2.5x</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            
            <div className="mt-12 p-10 bg-[#0A0B0F] text-white rounded-[40px] border border-white/10 flex flex-col items-center text-center gap-6 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 left-0 w-64 h-64 bg-nobel-gold opacity-[0.03] blur-3xl pointer-events-none"></div>
                <div className="absolute bottom-0 right-0 w-64 h-64 bg-blue-500 opacity-[0.03] blur-3xl pointer-events-none"></div>
                
                <div className="relative flex flex-col items-center">
                    <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center text-nobel-gold border border-white/10 mb-2">
                        <Lock size={32} />
                    </div>
                    <div className="absolute -top-3 -right-6 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-md shadow-lg border border-white/10 whitespace-nowrap z-20">
                        2.5x Power
                    </div>
                </div>

                <div className="max-w-2xl mx-auto px-4 relative z-10">
                    <div className="font-serif text-2xl md:text-3xl font-bold mb-3 tracking-tight text-white">Locked Reserves (Streamflow Finance)</div>
                    <p className="text-sm md:text-base text-stone-400 leading-relaxed mx-auto max-w-lg">
                        3,000,000,000 GHOST (15% of supply) are locked for 24 months via Streamflow. Linear release schedule beginning <span className="text-nobel-gold font-bold">Q1 2026</span> ensures maximum protocol stability and long-term ecosystem alignment.
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 w-full max-w-sm justify-center mt-4 relative z-10">
                    <button className="px-8 py-3 rounded-xl bg-white text-black font-bold text-xs uppercase tracking-widest hover:bg-stone-200 transition-all flex items-center justify-center gap-2 flex-1">
                        Verify <ArrowUpRight size={14}/>
                    </button>
                    <button className="px-8 py-3 rounded-xl bg-white/5 border border-white/10 text-white font-bold text-xs uppercase tracking-widest hover:bg-white/10 transition-all flex items-center justify-center gap-2 flex-1">
                        Lock Analytics <Star size={14} className="text-nobel-gold" />
                    </button>
                </div>
            </div>
        </section>
    </div>
);
